import { useEffect, useState } from 'react';
import MentorPortalNav from './MentorPortalNav';
import MentorDashboardChrome from './MentorDashboardChrome';
import MentorWelcomeBanner from './MentorWelcomeBanner';
import MentorKpiCards from './MentorKpiCards';
import MentorNextSessionPlaceholder from './MentorNextSessionPlaceholder';
import MentorMenteesWidget from './MentorMenteesWidget';
import MentorRecentMessagesWidget from './MentorRecentMessagesWidget';
import MentorPortalService, { MentorDashboardPayload, MentorMenteeCard } from './MentorPortalService';
import MentorMessagesService, { MentorConversationRow } from './MentorMessagesService';
import { EMPTY_MENTOR_COPY } from './mentorDashboardFormat';
import './mentorDashboard.css';

const MentorDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [dashboard, setDashboard] = useState<MentorDashboardPayload | null>(null);
    const [menteeCards, setMenteeCards] = useState<MentorMenteeCard[]>([]);
    const [conversations, setConversations] = useState<MentorConversationRow[]>([]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            MentorPortalService.loadDashboardView(),
            MentorMessagesService.listConversations().catch(() => [] as MentorConversationRow[]),
        ])
            .then(([view, listed]) => {
                if (cancelled) return;
                setDashboard(view.dashboard);
                setMenteeCards(view.menteeCards);
                setConversations(listed);
                setLoadError(null);
            })
            .catch((err: { status?: number; response?: { status?: number }; message?: string }) => {
                if (cancelled) return;
                const status = err?.status ?? err?.response?.status;
                if (status === 403 || status === 401) {
                    setDashboard(null);
                    setMenteeCards([]);
                    setLoadError(null);
                    return;
                }
                setLoadError(err?.message || 'Unable to load mentorship data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const isMentor = Boolean(dashboard?.mentor);
    const isEmpty = !loading && !loadError && !isMentor;
    const assigned = dashboard?.kpis?.menteesAssigned ?? menteeCards.length;

    return (
        <div className="container py-4 mdash">
            
            <MentorPortalNav />
            <MentorDashboardChrome />
            {loading && <p className="mdash-empty">Loading…</p>}
            {!loading && loadError && <p className="mdash-empty">{loadError}</p>}
            {isEmpty && <p className="mdash-empty">{EMPTY_MENTOR_COPY}</p>}
            {!loading && !loadError && isMentor && dashboard?.kpis && (
                <>
                    {dashboard.mentor ? (
                        <MentorWelcomeBanner mentor={dashboard.mentor} menteeCount={assigned} />
                    ) : null}
                    <MentorKpiCards kpis={dashboard.kpis} />
                    <MentorNextSessionPlaceholder />
                    <div className="row g-3">
                        <MentorMenteesWidget mentees={menteeCards} />
                        <MentorRecentMessagesWidget conversations={conversations} />
                    </div>
                </>
            )}
        </div>
    );
};

export default MentorDashboard;
